<template>
  <div>
    <v-card-title>
      <v-spacer />
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Filter"
      />
    </v-card-title>

    <v-tooltip
      top
      dark
    >
      <template v-slot:activator="{ on }">
        <v-btn
          color="secondary"
          fab
          dark
          absolute
          top
          right
          @click.stop="csvUploadingGlobalDialog=true"
          v-on="on"
        >
          <v-icon>publish</v-icon>
        </v-btn>
      </template>
      <span>Import Holders</span>
    </v-tooltip>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :pagination.sync="pagination"
      :loading="isLoading"
      loading-text="Loading..."
      disable-initial-sort
      :rows-per-page-items="[25,50,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <tr
          @click="toggleSelected(props.item)"
        >
          <td>
            {{ props.item.description }}
          </td>
          <td>
            {{ parseFloat(props.item.amount)|toCurrency }}
          </td>
          <td>
            {{ props.item.security_id }}
          </td>
          <td>
            {{ types()[props.item.type] }}
          </td>
          <td>
            <DateDisplay
              :date="props.item.expected_payout_date"
            />
          </td>
          <td class="text-xs">
            <v-chip
              v-if="props.item.errors"
              class="ma-2"
              color="red"
              text-color="white"
              @click.stop="toggleErrors(props.item.errors)"
            >
              Errors!
            </v-chip>
            <v-tooltip
              v-if="isAutomaticCashbackPayout(props.item)"
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="() => showApprovalDialog(props.item)"
                >
                  <v-icon>check</v-icon>
                </v-btn>
              </template>
              <span>Approve</span>
            </v-tooltip>
            <v-tooltip
              v-if="props.item.security && props.item.security.type === 'yield_baader'"
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="error"
                  v-on="on"
                  @click.stop="() => showCancelDialog(props.item)"
                >
                  <v-icon>cancel</v-icon>
                </v-btn>
              </template>
              <span>Decline</span>
            </v-tooltip>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="() => showDialog(props.item)"
                >
                  <v-icon>publish</v-icon>
                </v-btn>
              </template>
              <span>Upload CSV</span>
            </v-tooltip>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="error"
                  v-on="on"
                  @click.stop="() => showDeleteDialog(props.item)"
                >
                  <v-icon>delete</v-icon>
                </v-btn>
              </template>
              <span>Delete Payout</span>
            </v-tooltip>
          </td>
        </tr>
        <tr v-if="displayErrors === props.item.errors">
          <td colspan="12">
            <ErrorDetails
              :errors="props.item.errors"
            />
          </td>
        </tr>
        <tr v-if="props.item.payout_id && selected === props.item.payout_id">
          <td colspan="6">
            <PayoutDetails
              :payout="props.item"
              :holders="props.item.holders"
              :show-holders="true"
              :show-holders-payback-information="true"
              :allow-to-delete-holders="true"
            />
          </td>
        </tr>
      </template>
    </v-data-table>

    <HolderEnrichmentGlobalUpload
      :visible="csvUploadingGlobalDialog"
      @close="csvUploadingGlobalDialog=false"
      @fileUpload="(file) => onUploadGlobalCsv(file)"
    />

    <HolderEnrichmentUploadCSVDialog
      key="upload_dialog"
      :visible="isDialogActive()"
      :payout="currentPayout"
      @close="hideDialog()"
      @fileUpload="(file) => onUploadCsv(file)"
    />

    <ApprovalsApproveDialog
      key="approve_dialog"
      :visible="isApproveDialogActive()"
      :payout="currentPayout"
      @close="hideApprovalDialog()"
      @submit="onApprove()"
    />

    <ApprovalsCancelDialog
      key="decline_dialog"
      :visible="isCancelDialogActive()"
      :payout="currentPayout"
      @close="hideCancelDialog()"
      @submit="onCancel()"
    />

    <DeleteDialog
      key="delete_dialog"
      :visible="isDeleteDialogActive()"
      :payout="currentPayout"
      @close="hideDeleteDialog()"
      @submit="onDelete()"
    />

    <v-dialog
      v-model="csvUploadingDialog"
      persistent
      width="300"
    >
      <v-card
        color="primary"
        dark
      >
        <v-card-text>
          Please wait while uploading CSV.
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="csvResultDialog"
      width="800"
    >
      <v-card
        color="primary"
        dark
        class="pa-3"
      >
        <p class="font-weight-black">
          Uploading CSV finished - Result:
        </p>
        <p>Total in CSV: {{ csvUploadingResult.totalInCsv }}</p>
        <p>Total Valid: {{ csvUploadingResult.totalImported }}</p>
        <p>Total Saved to DB: {{ csvUploadingResult.totalDBInserts }}</p>
        <p>Total Failed: {{ csvUploadingResult.totalFailed }}</p>
        <p
          v-for="item in csvUploadingResult.failedReasons"
          :key="item.index"
          style="background-color: red;"
          class="pa-1"
        >
          {{ item.message }}
        </p>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snackbarShow"
      :color="snackbarColor"
      :auto-height="true"
    >
      <ul v-if="snackbarErrors">
        <li
          v-for="snackbarError in snackbarErrors"
          :key="snackbarError.message"
        >
          {{ snackbarError.message }}
        </li>
      </ul>

      <div
        v-if="snackbarMessage"
      >
        {{ snackbarMessage }}
      </div>

      <v-btn
        color="primary"
        text
        @click="snackbarShow = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import ApprovalsApproveDialog from './dialogs/payout/ApprovalsApproveDialog';
import HolderEnrichmentUploadCSVDialog from './dialogs/payout/HolderEnrichmentUploadCSVDialog';
import ApprovalsCancelDialog from './dialogs/payout/ApprovalsCancelDialog';
import PayoutDetails from '@/components/payoutsAndTaxes/payout/shared/PayoutDetails';
import DeleteDialog from '@/components/payoutsAndTaxes/payout/dialogs/payout/DeleteDialog';
import DateDisplay from '@/components/payoutsAndTaxes/payout/shared/DateDisplay';
import ErrorDetails from '@/components/payoutsAndTaxes/payout/shared/ErrorDetails';
import HolderEnrichmentGlobalUpload from '@/components/payoutsAndTaxes/payout/dialogs/payout/HolderEnrichmentGlobalUpload.vue';

export default {
  name: 'PayoutApprovals',
  components: {
    HolderEnrichmentGlobalUpload,
    HolderEnrichmentUploadCSVDialog,
    PayoutDetails,
    ApprovalsApproveDialog,
    ApprovalsCancelDialog,
    DeleteDialog,
    DateDisplay,
    ErrorDetails,
  },
  data() {
    return {
      snackbarShow: false,
      snackbarMessage: '',
      snackbarColor: 'error',
      snackbarErrors: [],
      csvUploadingDialog: false,
      csvUploadingResult: {
        totalDBInserts: 0,
        totalFailed: 0,
        totalImported: 0,
        totalInCsv: 0,
        failedReasons: [],
      },
      csvUploadingGlobalDialog: false,
      displayErrors: {},
      csvResultDialog: false,
      selected: 0,
      showContractId: null,
      isLoading: false,
      isCreating: false,
      isShowing: false,
      isEditing: false,
      currEditSigning: {},
      currentPayout: {},
      uploadCSVDialog: false,
      deleteDialog: false,
      pagination: {
        rowsPerPage: 25,
      },
      search: '',
      headers: [
        { text: 'Payout', value: 'payout_id' },
        { text: 'Amount', value: 'amount' },
        { text: 'Security ID', value: 'security_id' },
        { text: 'Type', value: 'type' },
        { text: 'Expected Payout Date', value: 'expected_payout_date' },
        { text: 'Actions', value: 'actions' },
      ],
    };
  },
  computed: {
    payoutsHolderEnrichmentPending() {
      return this.$store.getters.getPayoutsHolderEnrichmentPending;
    },
    filteredItems() {
      const { search } = this;

      return search.length < 1 ?
        this.payoutsHolderEnrichmentPending :
        this.payoutsHolderEnrichmentPending.filter((item) => {
          let found = false;
          Object.keys(item)
            .map((key) => {
              if (item[key] && String(item[key]).toLowerCase().indexOf(search.toLowerCase()) > -1) {
                found = true;
              }

              return key;
            });

          return found;
        });
    },
  },
  methods: {
    types() {
      const data = this.$store.getters.getPayoutsCreationTypes;
      if (!data || !data.payout || !data.payout.types) {
        return {};
      }

      return data.payout.types;
    },
    onApprove() {
      this.$store.dispatch('callPayoutHolderEnrichmentApproval', this.currentPayout.payout_id)
        .then(() => {
          this.snackbarShow = true;
          this.snackbarErrors = [];
          this.snackbarMessage = 'Payout successfully approved';
          this.snackbarColor = 'success';
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    isCancelDialogActive() {
      return this.cancelDialog === true;
    },
    onCancel() {
      this.$store.dispatch('callPayoutHolderApproval', { payoutId: this.currentPayout.payout_id, approve: false })
        .then(() => {
          this.snackbarShow = true;
          this.snackbarErrors = [];
          this.snackbarMessage = 'Payout declined!';
          this.snackbarColor = 'success';
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    isDialogActive() {
      return this.uploadCSVDialog === true;
    },
    isApproveDialogActive() {
      return this.approvalDialog === true;
    },
    showDialog(item) {
      this.currentPayout = item;
      this.uploadCSVDialog = true;
      this.$forceUpdate();
    },
    showApprovalDialog(item) {
      this.currentPayout = item;
      this.approvalDialog = true;
      this.$forceUpdate();
    },
    isAutomaticCashbackPayout(payout) {
      return payout.type === 'cashback_v2';
    },
    toggleErrors(errors) {
      this.displayErrors = this.displayErrors === errors ? null : errors;
    },
    hideDialog() {
      this.uploadCSVDialog = false;
      this.$forceUpdate();
    },
    hideApprovalDialog() {
      this.approvalDialog = false;
      this.$forceUpdate();
    },
    showCancelDialog(item) {
      this.currentPayout = item;
      this.cancelDialog = true;
      this.$forceUpdate();
    },
    hideCancelDialog() {
      this.cancelDialog = false;
      this.$forceUpdate();
    },
    onUploadCsv(file) {
      const payoutId = this.currentPayout.payout_id;
      this.csvUploadingDialog = true;
      this.$store
        .dispatch('callPayoutHolderEnrichmentImportCSV', { file, payoutId })
        .then((e) => {
          this.csvUploadingDialog = false;
          this.csvUploadingResult = e;
          this.csvResultDialog = true;
          this.$store.dispatch('fetchPayoutAll');
        })
        .catch((error) => {
          this.csvUploadingDialog = false;
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    onUploadGlobalCsv(file) {
      this.csvUploadingDialog = false;
      this.$store
        .dispatch('callPayoutImportGlobalCSV', { file })
        .then((e) => {
          this.csvUploadingGlobalDialog = false;
          this.csvUploadingResult = e;
          this.csvResultDialog = true;
          this.$store.dispatch('fetchPayoutAll');
        })
        .catch((error) => {
          this.csvUploadingGlobalDialog = false;
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    onDelete() {
      this.$store.dispatch('callPayoutDelete', this.currentPayout.payout_id)
        .then(() => {
          this.snackbarShow = true;
          this.snackbarErrors = [];
          this.snackbarMessage = 'Payout Deleted!';
          this.snackbarColor = 'success';
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    isDeleteDialogActive() {
      return this.deleteDialog === true;
    },
    showDeleteDialog(item) {
      this.currentPayout = item;
      this.deleteDialog = true;
      this.$forceUpdate();
    },
    hideDeleteDialog() {
      this.deleteDialog = false;
      this.$forceUpdate();
    },
    toggleSelected(payout) {
      this.selected = this.selected === payout.payout_id ? null : payout.payout_id;
      this.loadPayoutHolders(payout);
    },
    loadPayoutHolders(payout) {
      if (payout.holders && payout.holders.length !== 0) {
        return;
      }

      this.$store.dispatch('fetchPayoutDataGetHolders', {
        origin: 'holderEnrichment',
        payoutId: payout.payout_id,
      })
        .then(() => {
          this.$forceUpdate();
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
  },
};
</script>
<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/HolderEnrichment.vue